import React, { useEffect, useState } from "react";
import { useLazyGetAcademyHomeDataQuery } from "../../store/api/academyApi";
import Button from "../common/button";
import moment from "moment";
import { EventLocation } from "../../icons/tags-icons";
import { AcademyCalender } from "../../icons/academy-icons";
import { Link } from "react-router-dom";
import LockedOverlay from "../common/locked_content";
import { useAppDispatch, useAppSelector } from "../../hooks/store-hooks";
import { openModal } from "../../store/slices/modalSlice";
import { MODAL_NAME } from "../../constants";
import BecomeMember from "../common/membership";

interface Livestream {
  livestream_id: string;
  title: string;
  thumbnail: string;
  date: string;
  location: string;
  is_subscribed?: boolean;
}

function LiveStreamComponent() {
  const [triggerGetAcademyAllTabData] = useLazyGetAcademyHomeDataQuery();
  const [livestreamData, setLivestreamData] = useState<{
    livestreams_list: Livestream[];
  }>({ livestreams_list: [] });
  const [page, setPage] = useState(1);
  const [isEnd, setIsEnd] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const itemsPerPage = 21;
  const dispatch = useAppDispatch();
  const { modalName } = useAppSelector((state) => state.modal);

  const fetchLivestreamData = async (offset: number) => {
    if (isLoading || isEnd) return;

    setIsLoading(true);
    try {
      const resp = await triggerGetAcademyAllTabData({
        type: "livestream",
        offset,
        limit: itemsPerPage,
      }).unwrap();

      if (resp.data.livestreams_list.length < itemsPerPage) {
        setIsEnd(true);
      }

      setLivestreamData((prevData) => ({
        livestreams_list:
          offset === 0
            ? resp.data.livestreams_list
            : [...prevData.livestreams_list, ...resp.data.livestreams_list],
      }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isEnd && !isLoading) {
      const offset = (page - 1) * itemsPerPage;
      fetchLivestreamData(offset);
    }
  }, [page]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !isEnd && !isLoading) {
        setPage((prevPage) => prevPage + 1);
      }
    });

    const lastElement = document.querySelector("#infinite-scroll-trigger");
    if (lastElement) {
      observer.observe(lastElement);
    }

    return () => {
      if (lastElement) {
        observer.unobserve(lastElement);
      }
    };
  }, [isEnd, isLoading]);

  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="p-8 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Livestream</h2>
        <div className="flex flex-wrap gap-6 w-full">
          {livestreamData.livestreams_list.map(
            (livestream: Livestream, index: number) => (
              <div
                key={index}
                className="bg-white !w-80 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="relative">
                  <img
                    src={livestream.thumbnail}
                    alt={livestream.title}
                    className="w-full object-cover"
                  />
                  {livestream.is_subscribed ? null : (
                    <LockedOverlay blur={false} />
                  )}
                </div>
                <div className="p-4">
                  <p className="text-xl font-bold">{livestream.title}</p>
                  <div className="flex gap-2 items-center pt-2 pb-2">
                    <AcademyCalender width={20} />
                    <p className="text-sm text-[#404040]">
                      {moment(livestream.date).format(
                        "DD MMM YYYY, ddd, hh:mm A",
                      )}
                    </p>
                  </div>
                  {livestream?.location && (
                    <div className="flex gap-2 items-center pb-2">
                      <EventLocation
                        width={20}
                        height={32}
                        strokeColor="#404040"
                      />{" "}
                      <p className="text-sm text-[#404040]">
                        {livestream.location}
                      </p>
                    </div>
                  )}
                  {livestream?.is_subscribed ? (
                    <Link to={livestream.livestream_id}>
                      <Button customClasses="py-1 font-semibold">
                        View Recording
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      customClasses="py-1 font-semibold"
                      onClick={() => {
                        dispatch(
                          openModal({
                            modalName: MODAL_NAME.BECOME_MEMBER,
                          }),
                        );
                      }}
                    >
                      View Recording
                    </Button>
                  )}
                </div>
              </div>
            ),
          )}
        </div>
        <div id="infinite-scroll-trigger" style={{ height: "20px" }} />
      </div>
      {modalName === MODAL_NAME.BECOME_MEMBER && <BecomeMember />}
    </div>
  );
}

export default LiveStreamComponent;
